import React, { useEffect, useState, useMemo } from 'react';
import { useToasts } from 'react-toast-notifications';
import { Row, Col, Button, Input, Card, CardBody, Table, FormGroup, Label, Badge } from 'reactstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { withTranslation } from 'react-multi-lang';
import { DateTime } from 'luxon';
import CheckInsService from '../Services/CheckInsService';
import UsersService from '../Services/UsersService';
import AuthService from '../Services/AuthService';
import { DionysosService } from '../Services/DionysosService';

const parseToAthens = (d) => {
    if (!d) return null;
    let dt = null;
    if (d instanceof Date) {
        dt = DateTime.fromJSDate(d).setZone('Europe/Athens');
    } else if (typeof d === 'string') {
        const hasZone = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i.test(d);
        dt = DateTime.fromISO(d, { zone: hasZone ? undefined : 'utc', setZone: true });
        if (!dt.isValid) {
            dt = DateTime.fromSQL(d, { zone: hasZone ? undefined : 'utc', setZone: true });
        }
        if (dt.isValid) {
            dt = dt.setZone('Europe/Athens');
        } else {
            const jsDate = new Date(d);
            if (!isNaN(jsDate.getTime())) {
                dt = DateTime.fromJSDate(jsDate).setZone('Europe/Athens');
            }
        }
    } else {
        const jsDate = new Date(d);
        if (!isNaN(jsDate.getTime())) {
            dt = DateTime.fromJSDate(jsDate).setZone('Europe/Athens');
        }
    }
    return dt && dt.isValid ? dt : null;
};

const formatDate = (d) => {
    const dt = parseToAthens(d);
    return dt ? dt.toFormat('dd/MM/yyyy') : '';
};

const formatTime = (d) => {
    const dt = parseToAthens(d);
    return dt ? dt.toFormat('HH:mm') : '-';
};

const formatDateTime = (d) => {
    const dt = parseToAthens(d);
    return dt ? dt.toFormat('dd/MM/yyyy HH:mm') : '';
};

const getEffectiveTimeFrom = (c) => {
    if (!c) return null;
    const timeFrom = c.timeFrom;
    const createdAt = c.createdAt;
    if (timeFrom && createdAt) {
        const fromMs = (timeFrom instanceof Date ? timeFrom : new Date(timeFrom)).getTime();
        const createdMs = (createdAt instanceof Date ? createdAt : new Date(createdAt)).getTime();
        if (!isNaN(fromMs) && !isNaN(createdMs)) {
            const diffSecs = (createdMs - fromMs) / 1000;
            if (diffSecs >= 7000 && diffSecs <= 11000) {
                return createdAt;
            }
        }
    }
    return timeFrom;
};

const CheckIns = ({ t }) => {
    const [checkIns, setCheckIns] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalDurationMs, setTotalDurationMs] = useState(0);
    const [totalWaitingTime, setTotalWaitingTime] = useState(0);
    const [users, setUsers] = useState([]);
    const [machines, setMachines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [filters, setFilters] = useState({
        page: 1,
        limit: 10,
        userId: '',
        machineId: '',
        area: '',
        dateFrom: '',
        dateTo: '',
        wh: ''
    });

    const [expandedRows, setExpandedRows] = useState([]);
    const [childCheckIns, setChildCheckIns] = useState({});

    const checkInsService = new CheckInsService();
    const usersService = new UsersService();
    const authService = new AuthService();
    const dionysosService = new DionysosService();
    const { addToast } = useToasts();

    const currentConfig = authService.getConfig();
    const shelves = currentConfig ? currentConfig.shelves : [];

    useEffect(() => {
        if (currentConfig && currentConfig.whCode) {
            setFilters(prev => ({ ...prev, wh: currentConfig.whCode }));
        }
    }, [currentConfig?.whCode]);

    useEffect(() => {
        getUsers();
        getMachines();
    }, []);

    useEffect(() => {
        fetchCheckIns();
    }, [filters.page, filters.wh]);

    const getUsers = () => {
        usersService.getUsers().then(r => {
            setUsers(r);
        }).catch(e => console.error(e));
    };

    const getMachines = () => {
        dionysosService.getMachines().then(r => {
            setMachines(r);
        }).catch(e => console.error(e));
    };

    const fetchCheckIns = () => {
        setLoading(true);
        checkInsService.getCheckIns({ ...filters, onlyParents: true }).then(r => {
            setCheckIns(r.data);
            setTotal(r.total);
            setTotalDurationMs(r.totalDurationMs || 0);
            setTotalWaitingTime(r.totalWaitingTime || 0);
            setLoading(false);
        }).catch(e => {
            addToast(e, { appearance: 'error', autoDismiss: true });
            setLoading(false);
        });
    };

    const toggleRow = (id) => {
        const isExpanded = expandedRows.includes(id);
        if (isExpanded) {
            setExpandedRows(expandedRows.filter(rowId => rowId !== id));
        } else {
            setExpandedRows([...expandedRows, id]);
            if (!childCheckIns[id]) {
                checkInsService.getCheckIns({ parentId: id, limit: 100 }).then(r => {
                    setChildCheckIns(prev => ({ ...prev, [id]: r.data }));
                }).catch(e => {
                    addToast(e, { appearance: 'error', autoDismiss: true });
                });
            }
        }
    };

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value, page: 1 });
    };

    const handleUserChange = (selectedOption) => {
        setFilters({ ...filters, userId: selectedOption ? selectedOption.value : '', page: 1 });
    };

    const handleMachineChange = (selectedOption) => {
        setFilters({ ...filters, machineId: selectedOption ? selectedOption.value : '', page: 1 });
    };

    const userOptions = useMemo(() => {
        return [
            { value: '', label: t('checkIns.allUsers') },
            ...users.map(u => ({ value: u.userId, label: u.name }))
        ];
    }, [users, t]);

    const machineOptions = useMemo(() => {
        return [
            { value: '', label: t('checkIns.allMachines') },
            ...machines.map(m => ({ value: m.Code, label: m.Description }))
        ];
    }, [machines, t]);

    const applyFilters = () => {
        setFilters({ ...filters, page: 1 });
        fetchCheckIns();
    };

    const exportCheckIns = () => {
        checkInsService.exportCheckIns(filters);
    };

    const handleSyncUnsynced = () => {
        setSyncing(true);
        checkInsService.syncUnsynced().then((res) => {
            const syncedCount = res && res.synced !== undefined ? res.synced : 0;
            const totalCount = res && res.total !== undefined ? res.total : 0;
            addToast(t('checkIns.syncSuccess') || `Συγχρονίστηκαν ${syncedCount} από ${totalCount} check-ins με το ERP`, { appearance: 'success', autoDismiss: true });
            fetchCheckIns();
        }).catch((err) => {
            addToast(err.toString(), { appearance: 'error', autoDismiss: true });
        }).finally(() => {
            setSyncing(false);
        });
    };

    const nextPage = () => {
        if (filters.page * filters.limit < total) {
            setFilters({ ...filters, page: filters.page + 1 });
        }
    };

    const prevPage = () => {
        if (filters.page > 1) {
            setFilters({ ...filters, page: filters.page - 1 });
        }
    };

    const formatDuration = (timeFrom, timeTo) => {
        if (!timeFrom || !timeTo) return '-';
        const fromDt = parseToAthens(timeFrom);
        const toDt = parseToAthens(timeTo);
        if (!fromDt || !toDt) return '-';

        const from = fromDt.startOf('minute');
        const to = toDt.startOf('minute');

        const diffMs = to.toMillis() - from.toMillis();
        return formatMsDuration(diffMs);
    };

    const formatMsDuration = (diffMs) => {
        if (diffMs <= 0) return '0h';
        
        const diffTotalMins = Math.floor(diffMs / (1000 * 60));
        const diffHrs = Math.floor(diffTotalMins / 60);
        const diffMins = diffTotalMins % 60;
        
        if (diffMins === 0) {
            return `${diffHrs}h`;
        }
        
        return `${diffHrs}h ${diffMins}m`;
    };

    const getCompanyDisplay = (c) => {
        if (!c) return '-';
        if (c.company) return c.company;
        const wh = (c.wh || '').toString().trim();
        if (['219', '211', '007', '7'].includes(wh)) return '007';
        if (['4', '2', '5', '001', '1'].includes(wh)) return '001';
        return '003';
    };

    return (
        <Card style={{ padding: '20px' }}>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3>{t('checkIns.title')}</h3>
                    <div>
                        <Button color="primary" className="me-2" onClick={handleSyncUnsynced} disabled={syncing}>
                            <FontAwesomeIcon icon={faSync} spin={syncing} className="me-1" /> {t('checkIns.syncUnsynced') || 'Συγχρονισμός με ERP'}
                        </Button>
                        <Button color="success" onClick={exportCheckIns}>{t('checkIns.exportExcel')}</Button>
                    </div>
                </div>
                <Row className="mb-4">
                    <Col md={2}>
                        <FormGroup>
                            <Label>{t('checkIns.user')}</Label>
                            <Select
                                isClearable
                                isSearchable
                                name="userId"
                                value={userOptions.find(o => o.value === filters.userId)}
                                onChange={handleUserChange}
                                options={userOptions}
                                placeholder={t('checkIns.allUsers')}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={2}>
                        <FormGroup>
                            <Label>{t('checkIns.machine')}</Label>
                            <Select
                                isClearable
                                isSearchable
                                name="machineId"
                                value={machineOptions.find(o => o.value === filters.machineId)}
                                onChange={handleMachineChange}
                                options={machineOptions}
                                placeholder={t('checkIns.allMachines')}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={2}>
                        <FormGroup>
                            <Label>{t('checkIns.area')}</Label>
                            <Input type="select" name="area" value={filters.area} onChange={handleFilterChange}>
                                <option value="">{t('checkIns.allAreas')}</option>
                                {shelves.map(s => <option key={s} value={s}>{s}</option>)}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={2}>
                        <FormGroup>
                            <Label>{t('checkIns.dateFrom')}</Label>
                            <Input type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} />
                        </FormGroup>
                    </Col>
                    <Col md={2}>
                        <FormGroup>
                            <Label>{t('checkIns.dateTo')}</Label>
                            <Input type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} />
                        </FormGroup>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                        <FormGroup>
                            <Button color="primary" onClick={applyFilters}>{t('checkIns.filter')}</Button>
                        </FormGroup>
                    </Col>
                </Row>

                <Table responsive striped>
                    <thead>
                        <tr>
                            <th></th>
                            <th>{t('checkIns.user')}</th>
                            <th>{t('checkIns.company') || 'Εταιρεία'}</th>
                            <th>{t('checkIns.area')}</th>
                            <th>{t('checkIns.machine')}</th>
                            <th>{t('checkIns.type')}</th>
                            <th>{t('machineTypes.workTypeName') || 'Εργασία'}</th>
                            <th>{t('checkIns.date')}</th>
                            <th>{t('checkIns.timeFrom')}</th>
                            <th>{t('checkIns.timeTo')}</th>
                            <th>{t('checkIns.operationTime')}</th>
                            <th>{t('checkIns.totalDistance')}</th>
                            <th>{t('checkIns.machinePreparationTime')}</th>
                            <th>{t('checkIns.threadFishing')}</th>
                            <th>{t('checkIns.drilling')}</th>
                            <th>{t('checkIns.cuttingHours')}</th>
                            <th>{t('checkIns.cuttingArea')}</th>
                            <th>{t('checkIns.other')}</th>
                            <th>{t('checkIns.waitingTime')}</th>
                            <th>{t('checkIns.duration')}</th>
                            <th>{t('checkIns.createdAt')}</th>
                            <th>{t('checkIns.synced') || 'ERP'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {checkIns.map(c => {
                            const effectiveTimeFrom = getEffectiveTimeFrom(c);
                            return (
                            <React.Fragment key={c.id}>
                                <tr key={c.id} onClick={() => c.parentId === null && toggleRow(c.id)} style={{ cursor: c.parentId === null ? 'pointer' : 'default', fontWeight: c.parentId === null ? 'bold' : 'normal' }}>
                                    <td>{c.parentId === null && (expandedRows.includes(c.id) ? '▼' : '▶')}</td>
                                    <td key={c.id + 'user'}>{c.user ? c.user.name : (users.find(u => u.userId === c.userId)?.name || '')}</td>
                                    <td>{getCompanyDisplay(c)}</td>
                                    <td>{c.area}</td>
                                    <td>{machines.find(m => m.Code === c.machineId)?.Description || c.machineId}</td>
                                    <td>{c.type === 'WAITING' ? t('checkIns.typeWaiting') : t('checkIns.typeWork')}</td>
                                    <td>{c.workType || '-'}</td>
                                    <td>{formatDate(effectiveTimeFrom)}</td>
                                    <td>{formatTime(effectiveTimeFrom)}</td>
                                    <td>{formatTime(c.timeTo)}</td>
                                    <td>{c.operationTime || '-'}</td>
                                    <td>{c.totalDistance || '-'}</td>
                                    <td>{c.machinePreparationTime || '-'}</td>
                                    <td>{c.threadFishing || '-'}</td>
                                    <td>{c.drilling || '-'}</td>
                                    <td>{c.cuttingHours || '-'}</td>
                                    <td>{c.cuttingArea || '-'}</td>
                                    <td>{c.other || '-'}</td>
                                    <td>{c.waitingTime || '-'}</td>
                                    <td>{formatDuration(effectiveTimeFrom, c.timeTo)}</td>
                                    <td>{formatDateTime(c.createdAt)}</td>
                                    <td>
                                        {c.isSyncedWithERP ? (
                                            <Badge color="success">{t('faults.yes') || 'Ναι'}</Badge>
                                        ) : (
                                            <Badge color="danger">{t('faults.no') || 'Όχι'}</Badge>
                                        )}
                                    </td>
                                </tr>
                                {expandedRows.includes(c.id) && childCheckIns[c.id] && childCheckIns[c.id].map(child => {
                                    const childEffectiveTimeFrom = getEffectiveTimeFrom(child);
                                    return (
                                    <tr key={child.id} style={{ backgroundColor: '#f9f9f9', fontSize: '0.9em' }}>
                                        <td></td>
                                        <td>└</td>
                                        <td>{getCompanyDisplay(child)}</td>
                                        <td>{child.area}</td>
                                        <td>{machines.find(m => m.Code === child.machineId)?.Description || child.machineId}</td>
                                        <td>{child.type === 'WAITING' ? t('checkIns.typeWaiting') : t('checkIns.typeWork')}</td>
                                        <td>{child.workType || '-'}</td>
                                        <td>{formatDate(childEffectiveTimeFrom)}</td>
                                        <td>{formatTime(childEffectiveTimeFrom)}</td>
                                        <td>{formatTime(child.timeTo)}</td>
                                        <td>{child.operationTime || '-'}</td>
                                        <td>{child.totalDistance || '-'}</td>
                                        <td>{child.machinePreparationTime || '-'}</td>
                                        <td>{child.threadFishing || '-'}</td>
                                        <td>{child.drilling || '-'}</td>
                                        <td>{child.cuttingHours || '-'}</td>
                                        <td>{child.cuttingArea || '-'}</td>
                                        <td>{child.other || '-'}</td>
                                        <td>{child.waitingTime || '-'}</td>
                                        <td>{formatDuration(childEffectiveTimeFrom, child.timeTo)}</td>
                                        <td>{formatDateTime(child.createdAt)}</td>
                                        <td>
                                            {child.isSyncedWithERP ? (
                                                <Badge color="success">{t('faults.yes') || 'Ναι'}</Badge>
                                            ) : (
                                                <Badge color="danger">{t('faults.no') || 'Όχι'}</Badge>
                                            )}
                                        </td>
                                    </tr>
                                    );
                                })}
                            </React.Fragment>
                            );
                        })}
                        {checkIns.length === 0 && !loading && (
                            <tr>
                                <td colSpan="22" className="text-center">{t('checkIns.noData')}</td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                        {t('checkIns.showing')} {(filters.page - 1) * filters.limit + 1} - {Math.min(filters.page * filters.limit, total)} {t('checkIns.of')} {total}
                        {" "} / {totalDurationMs > 0 && (
                            <span className="ml-3 font-weight-bold">
                                {t('checkIns.workHours')}: {formatMsDuration(totalDurationMs)}
                            </span>
                        )}
                        {totalWaitingTime > 0 && (
                            <span className="ml-3 font-weight-bold">
                                / {t('checkIns.totalWaitingTime')}: {formatMsDuration(totalWaitingTime)}
                            </span>
                        )}
                        {(totalDurationMs > 0 || totalWaitingTime > 0) && (
                            <span className="ml-3 font-weight-bold" style={{ color: '#007bff' }}>
                                / {t('checkIns.totalHours')}: {formatMsDuration(totalDurationMs + totalWaitingTime)}
                            </span>
                        )}
                    </div>
                    <div>
                        <Button color="secondary" size="sm" onClick={prevPage} disabled={filters.page === 1} className="mr-2">
                            {t('checkIns.previous')}
                        </Button>
                        <Button color="secondary" size="sm" onClick={nextPage} disabled={filters.page * filters.limit >= total}>
                            {t('checkIns.next')}
                        </Button>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default withTranslation(CheckIns);
